import { getNextStepChipLabel, getNextStepChipLabelInfluencer } from './journeyChipLabels';
import { getWorkQueueStatusKey } from '../../hooks/collaborations/useBrandCollaborationWorkQueue';
import { buildJourneyListItem } from './collaborationJourneyStats';
import { enrichBrandOperationsItem } from './brandOperationsUtils';
import { enrichInfluencerOperationsItem } from './influencerOperationsUtils';
import {
  getBrandAcceptanceStatus,
  getInfluencerAcceptanceStatus,
  getJourneyDetailKey,
  isCancelledJourneyAcceptance,
  resolveCompletedAwareJourneyStepSource,
} from './JourneyAcceptanceHelpers';

const ACTIVE_STATUSES = new Set([
  'accepted',
  'approved',
  'active',
  'completed',
  'closed',
  'negotiating',
]);

function isActiveStatus(status) {
  return ACTIVE_STATUSES.has(String(status || '').toLowerCase());
}

function isNegotiatingStatus(status) {
  return String(status || '').toLowerCase() === 'negotiating';
}

/** Negotiation is in-progress work — show under Active, not the post-accept journey. */
function getNegotiatingStepMeta(item, role) {
  const lastSender = String(item?.last_negotiation_sender_type || '').toLowerCase();
  const waitingOnCreator = role === 'brand' && lastSender === 'brand';
  const waitingOnBrand = role === 'influencer' && lastSender === 'influencer';
  return {
    label: 'Negotiating',
    isWaiting: waitingOnCreator || waitingOnBrand,
  };
}

function negotiatingProgress() {
  return {
    currentIndex: 0,
    currentStep: 1,
    totalSteps: 1,
    completedCount: 0,
    currentStepKey: null,
  };
}

function buildNegotiatingOperationsItem({
  item,
  detailKey,
  isApplication,
  role,
  enrichItem,
}) {
  const stepSource = item;
  const stepMeta = getNegotiatingStepMeta(item, role);
  const baseItem = buildJourneyListItem({
    item,
    detailKey,
    isApplication,
    stepSource,
    stepMeta,
    acceptanceDetail: null,
    resolveJourneyProgress: negotiatingProgress,
    role,
  });
  return enrichItem(baseItem, stepSource);
}

function buildActiveOperationsItem({
  item,
  detailKey,
  isApplication,
  acceptanceDetail,
  acceptanceStatus,
  rowStatus,
  role,
  getStepMeta,
  enrichItem,
}) {
  const stepSource = resolveCompletedAwareJourneyStepSource(
    item,
    acceptanceDetail,
    acceptanceStatus,
    rowStatus,
  );
  const stepMeta = getStepMeta(stepSource);
  if (!stepMeta?.label) return null;

  const baseItem = buildJourneyListItem({
    item,
    detailKey,
    isApplication,
    stepSource,
    stepMeta,
    acceptanceDetail,
    role,
  });
  return enrichItem(baseItem, stepSource);
}

function mapJourneyRow({
  item,
  isApplication,
  acceptanceDetail,
  acceptanceStatus,
  role,
  getStepMeta,
  enrichItem,
}) {
  const detailKey = getJourneyDetailKey(isApplication, item.id);
  const rowStatus = item.status?.toLowerCase?.();

  if (isCancelledJourneyAcceptance(acceptanceStatus, rowStatus)) return null;

  if (isNegotiatingStatus(rowStatus)) {
    return buildNegotiatingOperationsItem({
      item,
      detailKey,
      isApplication,
      role,
      enrichItem,
    });
  }

  return buildActiveOperationsItem({
    item,
    detailKey,
    isApplication,
    acceptanceDetail,
    acceptanceStatus,
    rowStatus,
    role,
    getStepMeta,
    enrichItem,
  });
}

export function buildBrandCollaborationJourneyItems({
  applications = [],
  invites = [],
  collaborationStatuses = {},
}) {
  const rows = [
    ...invites.map((item) => ({ kind: 'invite', item })),
    ...applications.map((item) => ({ kind: 'application', item })),
  ];

  return rows
    .map(({ kind, item }) => {
      if (!isActiveStatus(item?.status)) return null;

      const isApplication = kind === 'application';
      const acceptanceDetail = collaborationStatuses[getWorkQueueStatusKey(kind, item.id)];
      return mapJourneyRow({
        item,
        isApplication,
        acceptanceDetail,
        acceptanceStatus: getBrandAcceptanceStatus(acceptanceDetail),
        role: 'brand',
        getStepMeta: getNextStepChipLabel,
        enrichItem: enrichBrandOperationsItem,
      });
    })
    .filter(Boolean);
}

export function buildInfluencerCollaborationJourneyItems({
  applications = [],
  invites = [],
  acceptanceDetails = {},
}) {
  return [...invites, ...applications]
    .filter((item) => isActiveStatus(item?.status))
    .map((item) => {
      const isApplication = Boolean(item.product_name && item.company_name);
      const detailKey = getJourneyDetailKey(isApplication, item.id);
      const acceptanceDetail = acceptanceDetails[detailKey];
      return mapJourneyRow({
        item,
        isApplication,
        acceptanceDetail,
        acceptanceStatus: getInfluencerAcceptanceStatus(acceptanceDetail),
        role: 'influencer',
        getStepMeta: getNextStepChipLabelInfluencer,
        enrichItem: enrichInfluencerOperationsItem,
      });
    })
    .filter(Boolean);
}
